import {ReportFilters, ReportType} from '@gatewee/types/report';
import {Task, TaskType} from '@gatewee/types/task';

export type TaskLabelTranslateFn = (
  key: string,
  params?: Record<string, string | number>,
) => string;

function asRecord(value: unknown): Record<string, unknown> | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return undefined;
  }
  return value as Record<string, unknown>;
}

function pickString(
  source: Record<string, unknown>,
  keys: string[],
): string | undefined {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'string' && value.trim().length > 0) {
      return value.trim();
    }
  }
  return undefined;
}

function pickNumber(
  source: Record<string, unknown>,
  keys: string[],
): number | undefined {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }
    if (typeof value === 'string' && value.trim() !== '') {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) {
        return parsed;
      }
    }
  }
  return undefined;
}

function isReportType(value: string): value is ReportType {
  return (
    value === 'invoices' ||
    value === 'transactions' ||
    value === 'withdrawals' ||
    value === 'provider_transactions'
  );
}

function formatFilterDate(value: unknown): string | undefined {
  if (typeof value !== 'string' || !value.trim()) {
    return undefined;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return undefined;
  }
  return date.toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function formatReportFilterRange(
  filters: ReportFilters | undefined,
  translate: TaskLabelTranslateFn,
): string | undefined {
  if (!filters) {
    return undefined;
  }

  const start = formatFilterDate(filters.startDate);
  const end = formatFilterDate(filters.endDate);

  if (start && end) {
    return translate('features.tasks.labels.dateRange', {start, end});
  }
  if (start) {
    return translate('features.tasks.labels.fromDate', {date: start});
  }
  if (end) {
    return translate('features.tasks.labels.untilDate', {date: end});
  }
  if (filters.status) {
    return filters.status;
  }

  return undefined;
}

function buildReportLabel(
  options: Record<string, unknown>,
  translate: TaskLabelTranslateFn,
): string | undefined {
  const reportType = pickString(options, ['reportType', 'type']);
  const filters = asRecord(options['filters']) as ReportFilters | undefined;

  if (reportType && isReportType(reportType)) {
    const base = translate(`features.tasks.labels.report.${reportType}`);
    const range = formatReportFilterRange(filters, translate);
    return range ? `${base} — ${range}` : base;
  }

  return undefined;
}

function buildWebhookLabel(
  options: Record<string, unknown>,
  translate: TaskLabelTranslateFn,
): string | undefined {
  const name = pickString(options, [
    'label',
    'name',
    'description',
    'event',
    'provider',
    'url',
  ]);

  if (name) {
    return translate('features.tasks.labels.webhook', {name});
  }

  return translate('features.tasks.labels.webhookGeneric');
}

function buildEmailLabel(
  options: Record<string, unknown>,
  translate: TaskLabelTranslateFn,
): string | undefined {
  const subject = pickString(options, ['subject', 'title', 'label']);
  if (subject) {
    return translate('features.tasks.labels.emailSubject', {subject});
  }

  const count = pickNumber(options, [
    'recipientCount',
    'recipients',
    'count',
    'pageSize',
  ]);
  if (count !== undefined) {
    return translate('features.tasks.labels.emailBatch', {count});
  }

  return translate('features.tasks.labels.emailGeneric');
}

const labelBuilders: Record<
  TaskType,
  (options: Record<string, unknown>, translate: TaskLabelTranslateFn) => string
> = {
  report: (options, translate) =>
    buildReportLabel(options, translate) ??
    translate('features.tasks.labels.reportGeneric'),
  webhook: (options, translate) =>
    buildWebhookLabel(options, translate) ??
    translate('features.tasks.labels.webhookGeneric'),
  email: (options, translate) =>
    buildEmailLabel(options, translate) ??
    translate('features.tasks.labels.emailGeneric'),
};

/** Human-readable primary label derived from task type and options. */
export function getTaskLabel(
  task: Pick<Task, 'type' | 'identifier' | 'options'>,
  translate: TaskLabelTranslateFn,
): string {
  const options = task.options ?? {};
  const explicit = pickString(options, ['label', 'title', 'displayName']);
  if (explicit) {
    return explicit;
  }

  const built = labelBuilders[task.type]?.(options, translate);
  return built?.trim() || task.identifier;
}

/** Secondary line — identifier when it adds context beyond the primary label. */
export function getTaskSubtitle(
  task: Pick<Task, 'type' | 'identifier' | 'options'>,
  translate: TaskLabelTranslateFn,
): string | undefined {
  const label = getTaskLabel(task, translate);
  if (label !== task.identifier) {
    return task.identifier;
  }

  if (task.type === 'report') {
    return formatReportFilterRange(
      asRecord(task.options?.['filters']) as ReportFilters | undefined,
      translate,
    );
  }

  return undefined;
}
